import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import {
  getSuitabilityIssueSummary,
  type ProductSuitabilityResult,
  type ProductWithSuitabilityMeta,
  type SuitabilityContext,
} from "@/lib/services/suitability-service";
import type { RiskModelFilterOptions } from "@/lib/types/goal";
import { cn } from "@/lib/utils";
import type { SuitabilityStatus } from "./custom-portfolio-builder-types";
import { T } from "./product-picker-table.i18n";

type ProductPickerTableProps = {
  productsWithSuitability: ProductWithSuitabilityMeta[];
  excludeProductIds: string[];
  suitabilityResults: Map<string, ProductSuitabilityResult>;
  suitabilityContext: SuitabilityContext | null;
  clientBypassesSuitability: boolean;
  riskModelOptions: RiskModelFilterOptions | null;
  selectedProductId: string;
  onSelectProduct: (productId: string) => void;
  defaultProductTypes?: Set<string>;
  defaultRiskLevel?: number | null;
  /** QL-383 — the goal's final risk score; pre-fills the min risk score filter. */
  defaultRiskScore?: number | null;
  defaultTimeHorizon?: number | null;
};

export function ProductPickerTable({
  productsWithSuitability,
  excludeProductIds,
  suitabilityResults,
  suitabilityContext,
  clientBypassesSuitability,
  riskModelOptions,
  selectedProductId,
  onSelectProduct,
  defaultProductTypes,
  defaultRiskLevel,
  defaultRiskScore,
  defaultTimeHorizon,
}: ProductPickerTableProps) {
  const t = useT(T);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProductTypes, setFilterProductTypes] = useState<Set<string>>(
    defaultProductTypes || new Set(),
  );
  const [filterRiskLevel, setFilterRiskLevel] = useState<number | null>(defaultRiskLevel ?? null);
  // QL-383 — kept as the raw input string so the field can be cleared; "" = no filter.
  const [filterRiskScore, setFilterRiskScore] = useState<string>(
    defaultRiskScore != null ? String(defaultRiskScore) : "",
  );
  const [filterTimeHorizon, setFilterTimeHorizon] = useState<number | null>(
    defaultTimeHorizon ?? null,
  );
  const riskScoreValue =
    filterRiskScore.trim() === "" || !Number.isFinite(Number(filterRiskScore))
      ? null
      : Number(filterRiskScore);

  // Derive product type options from productsWithSuitability
  const productTypeOptions = useMemo(() => {
    const types = new Map<string, string>();
    for (const p of productsWithSuitability) {
      if (p.productTypeId && p.productTypeName) {
        types.set(p.productTypeId, p.productTypeName);
      }
    }
    return Array.from(types.entries()).map(([id, name]) => ({ id, name }));
  }, [productsWithSuitability]);

  // QL-383 — a constraint's filter and column only show when at least one
  // product in the collection sets it, so a tenant that never uses e.g. min
  // risk score is not shown an all-"—" column. Derived from the full list, not
  // the search-filtered one. Product type data is already omitted server-side
  // while the collection has "Use product types" off, so the same rule covers it.
  const columns = useMemo(
    () => ({
      productType: productTypeOptions.length > 0,
      minRiskLevel: productsWithSuitability.some((p) => p.minRiskLevelOrder != null),
      minRiskScore: productsWithSuitability.some((p) => p.minRiskScore != null),
      minHorizon: productsWithSuitability.some((p) => p.minTimeHorizonOrder != null),
    }),
    [productsWithSuitability, productTypeOptions],
  );
  // Name, ISIN and Suitable are always shown.
  const visibleColumnCount = 3 + Object.values(columns).filter(Boolean).length;

  // Filter products
  const filteredProducts = useMemo(() => {
    let products = productsWithSuitability.filter((p) => !excludeProductIds.includes(p.id));

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      products = products.filter(
        (p) => p.name.toLowerCase().includes(query) || p.isin?.toLowerCase().includes(query),
      );
    }

    // Apply product type filter
    const allKnownTypesSelected =
      productTypeOptions.length > 0 &&
      productTypeOptions.every((opt) => filterProductTypes.has(opt.id));

    if (filterProductTypes.size > 0 && !allKnownTypesSelected) {
      products = products.filter(
        (p) => !p.productTypeId || filterProductTypes.has(p.productTypeId),
      );
    }

    // Apply risk level filter. `== null` (not falsy): order 0 is a real minimum,
    // matching the shared suitability engine.
    if (filterRiskLevel !== null) {
      products = products.filter(
        (p) => p.minRiskLevelOrder == null || filterRiskLevel >= p.minRiskLevelOrder,
      );
    }

    // QL-383 — apply min risk score filter. A product without a minimum always
    // passes; 0 is a legitimate minimum, hence `== null`.
    if (riskScoreValue !== null) {
      products = products.filter((p) => p.minRiskScore == null || riskScoreValue >= p.minRiskScore);
    }

    // Apply time horizon filter
    if (filterTimeHorizon !== null) {
      products = products.filter(
        (p) => p.minTimeHorizonOrder == null || filterTimeHorizon >= p.minTimeHorizonOrder,
      );
    }

    return products;
  }, [
    productsWithSuitability,
    excludeProductIds,
    searchQuery,
    filterProductTypes,
    filterRiskLevel,
    riskScoreValue,
    filterTimeHorizon,
    productTypeOptions,
  ]);

  // Determine suitability status for a product
  const getSuitabilityStatus = (product: ProductWithSuitabilityMeta): SuitabilityStatus => {
    if (clientBypassesSuitability) return "suitable";

    const suitability = suitabilityResults.get(product.id);
    if (!suitability) return "unknown";

    if (!suitability.isSuitable) return "unsuitable";

    // Check if we have context data to make determination
    const hasRiskContext = suitabilityContext?.goalRiskLevelOrder !== undefined;
    const hasHorizonContext = suitabilityContext?.goalTimeHorizonOrder !== undefined;
    const hasExperienceContext = !!suitabilityContext?.experience;

    if (product.minRiskLevelOrder !== undefined && !hasRiskContext) return "unknown";
    if (product.minRiskScore !== undefined && suitabilityContext?.goalRiskScore === undefined)
      return "unknown";
    if (product.minTimeHorizonOrder !== undefined && !hasHorizonContext) return "unknown";
    if (product.productTypeId && !hasExperienceContext) return "unknown";

    return "suitable";
  };

  return (
    <div className="space-y-4">
      <SearchInput
        data-dialog-guard-ignore
        placeholder={t.searchPlaceholder}
        value={searchQuery}
        onValueChange={setSearchQuery}
      />

      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Product Type Multi-Select */}
        {productTypeOptions.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[180px] justify-start">
                {filterProductTypes.size === 0
                  ? t.allTypes
                  : filterProductTypes.size === productTypeOptions.length
                    ? t.allTypes
                    : `${filterProductTypes.size} ${t.typesSelected}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-2" align="start">
              <div className="space-y-2">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Checkbox
                    id="select-all-types"
                    checked={filterProductTypes.size === productTypeOptions.length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFilterProductTypes(new Set(productTypeOptions.map((o) => o.id)));
                      } else {
                        setFilterProductTypes(new Set());
                      }
                    }}
                  />
                  <label htmlFor="select-all-types" className="text-sm cursor-pointer">
                    {t.selectAll}
                  </label>
                </div>
                {productTypeOptions.map((opt) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`type-${opt.id}`}
                      checked={filterProductTypes.has(opt.id)}
                      onCheckedChange={(checked) => {
                        const newSet = new Set(filterProductTypes);
                        if (checked) {
                          newSet.add(opt.id);
                        } else {
                          newSet.delete(opt.id);
                        }
                        setFilterProductTypes(newSet);
                      }}
                    />
                    <label htmlFor={`type-${opt.id}`} className="text-sm cursor-pointer">
                      {opt.name}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Risk Tolerance Filter */}
        {columns.minRiskLevel && riskModelOptions && riskModelOptions.riskLevels.length > 0 && (
          <Select
            value={filterRiskLevel?.toString() ?? "all"}
            onValueChange={(v) => setFilterRiskLevel(v === "all" ? null : parseInt(v, 10))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t.anyRiskTolerance} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.anyRiskTolerance}</SelectItem>
              {riskModelOptions.riskLevels.map((opt) => (
                <SelectItem key={opt.id} value={opt.order.toString()}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* QL-383 — Min Risk Score Filter (products whose minimum is above the value are hidden) */}
        {columns.minRiskScore && (
          <Input
            data-dialog-guard-ignore
            type="number"
            inputMode="numeric"
            className="w-[180px]"
            aria-label={t.riskScoreFilter}
            placeholder={t.riskScoreFilter}
            value={filterRiskScore}
            onChange={(e) => setFilterRiskScore(e.target.value)}
          />
        )}

        {/* Time Horizon Filter */}
        {columns.minHorizon && riskModelOptions && riskModelOptions.timeHorizons.length > 0 && (
          <Select
            value={filterTimeHorizon?.toString() ?? "all"}
            onValueChange={(v) => setFilterTimeHorizon(v === "all" ? null : parseInt(v, 10))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t.anyTimeHorizon} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.anyTimeHorizon}</SelectItem>
              {riskModelOptions.timeHorizons.map((opt) => (
                <SelectItem key={opt.id} value={opt.order.toString()}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Results Count */}
      <p className="text-sm text-muted-foreground">
        {filteredProducts.length} {t.productsFound}
      </p>

      {/* Product List Table */}
      <TooltipProvider>
        <div className="border rounded-lg overflow-hidden">
          <ScrollArea className="h-[320px]">
            <Table variant="interactive" surface={false}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.tableHeaderName}</TableHead>
                  <TableHead>{t.tableHeaderIsin}</TableHead>
                  {columns.productType && <TableHead>{t.tableHeaderType}</TableHead>}
                  {columns.minRiskLevel && <TableHead>{t.tableHeaderMinRisk}</TableHead>}
                  {columns.minRiskScore && <TableHead>{t.tableHeaderMinRiskScore}</TableHead>}
                  {columns.minHorizon && <TableHead>{t.tableHeaderMinHorizon}</TableHead>}
                  <TableHead>{t.tableHeaderSuitable}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumnCount}
                      className="text-center text-muted-foreground py-8"
                    >
                      {t.noProductsMatch}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => {
                    const suitabilityStatus = getSuitabilityStatus(product);
                    const isSelected = selectedProductId === product.id;
                    const suitability = suitabilityResults.get(product.id);

                    return (
                      <TableRow
                        key={product.id}
                        onClick={() => onSelectProduct(product.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelectProduct(product.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={isSelected}
                        className={cn(
                          isSelected
                            ? "bg-accent"
                            : suitabilityStatus === "unsuitable"
                              ? "bg-destructive/5 hover:bg-destructive/10"
                              : "hover:bg-accent/50",
                        )}
                      >
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {product.isin || "—"}
                        </TableCell>
                        {columns.productType && (
                          <TableCell className="text-muted-foreground">
                            {product.productTypeName || "—"}
                          </TableCell>
                        )}
                        {columns.minRiskLevel && (
                          <TableCell className="text-muted-foreground">
                            {product.minRiskLevelName || "—"}
                          </TableCell>
                        )}
                        {columns.minRiskScore && (
                          <TableCell className="text-muted-foreground">
                            {/* != null — 0 is a real score and must render as "0". */}
                            {product.minRiskScore != null ? String(product.minRiskScore) : "—"}
                          </TableCell>
                        )}
                        {columns.minHorizon && (
                          <TableCell className="text-muted-foreground">
                            {product.minTimeHorizonName || "—"}
                          </TableCell>
                        )}
                        <TableCell>
                          {suitabilityStatus === "unknown" ? (
                            <Badge variant="secondary" className="text-xs">
                              {t.unknown}
                            </Badge>
                          ) : suitabilityStatus === "suitable" ? (
                            <Badge variant="success" className="text-xs">
                              {t.suitable}
                            </Badge>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="destructive" className="text-xs cursor-help">
                                  {t.notSuitable}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                {suitability && getSuitabilityIssueSummary(suitability)}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </TooltipProvider>

      {/* Info for Professional/Eligible Counterparty clients */}
      {clientBypassesSuitability && (
        <p className="text-sm text-muted-foreground">
          {t.clientClassifiedAs} {suitabilityContext?.classification?.classificationType}{" "}
          {t.autoSuitableForAll}
        </p>
      )}

      {/* Suitability warning for selected product */}
      {selectedProductId &&
        !clientBypassesSuitability &&
        (() => {
          const suitability = suitabilityResults.get(selectedProductId);
          if (suitability && !suitability.isSuitable) {
            return (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">{t.suitabilityWarning}</p>
                  <p className="text-muted-foreground">{getSuitabilityIssueSummary(suitability)}</p>
                </div>
              </div>
            );
          }
          return null;
        })()}
    </div>
  );
}
